/**
 * Iter 185 — Site-wide Header & Footer.
 * ============================================================================
 *
 * User request: header/footer that appears on EVERY page of the client's
 * website automatically (matching Shopify/Wix/Squarespace behaviour).
 *
 * This module exports:
 *   • `useSiteChrome()` — hook that fetches + caches the header/footer
 *     config from /api/builder/site/chrome. Returns `{ header, footer,
 *     save, loading }`. `save({ header?, footer? })` is a partial update.
 *
 *   • `<SiteHeader />` — renders the header from config. In edit mode
 *     (editable=true), clicking anywhere opens the Header inspector via
 *     the provided `onEdit` callback.
 *
 *   • `<SiteFooter />` — same pattern for footer.
 *
 * Both components are pure display + click-to-edit. All actual editing
 * happens in the EditDrawer inspectors (see SiteChromeInspectors.jsx),
 * which call `save()` from `useSiteChrome`.
 *
 * Component identity is STABLE (iter 183) — the inline text logo uses
 * the module-level `<StableInlineE />` from `content-editor/DashboardParts`,
 * passed via `E` prop, so no re-mount cycles trigger the flicker bug.
 */
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Search, ShoppingBag, Menu as MenuIcon, Instagram, Facebook, Twitter, Youtube, Linkedin, Music2 } from 'lucide-react';
import { CartDrawerTrigger } from '@/components/storefront/CartDrawer';
import SiteHeaderAccountLink from '@/components/storefront/SiteHeaderAccountLink';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SOCIAL_ICON = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  x: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
  tiktok: Music2,
};

/* ─────────────────────────────────────────────────────────────
 *  Data hook
 * ─────────────────────────────────────────────────────────── */
export function useSiteChrome() {
  const [header, setHeader] = useState(null);
  const [footer, setFooter] = useState(null);
  const [loading, setLoading] = useState(true);
  const inflight = useRef(false);

  const fetchChrome = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/builder/site/chrome`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHeader(data.header);
      setFooter(data.footer);
    } catch (e) {
      // Non-fatal — public pages / signed-out sessions just show defaults.
    } finally {
      inflight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChrome(); }, [fetchChrome]);

  const save = useCallback(async (partial) => {
    const token = localStorage.getItem('token');
    const { data } = await axios.put(`${API}/builder/site/chrome`, partial, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (data.header) setHeader(data.header);
    if (data.footer) setFooter(data.footer);
    return data;
  }, []);

  return { header, footer, loading, save, reload: fetchChrome };
}

/* ─────────────────────────────────────────────────────────────
 *  <SiteHeader />
 *  Props:
 *    header    — the config object (from useSiteChrome or a public fetch)
 *    editable  — when true, wraps content in an outline + click handler
 *    onEdit    — called when the user clicks the header to open the inspector
 *    E         — stable inline-edit component (only used in editable mode)
 *    onLogoTextChange(newText)  — inline-edit save callback
 * ─────────────────────────────────────────────────────────── */
export function SiteHeader({ header, editable = false, onEdit, E, onLogoTextChange }) {
  const items = useMemo(
    () => (header?.menu_items || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [header?.menu_items]
  );
  const buttons = useMemo(
    () => (header?.buttons || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [header?.buttons]
  );
  if (!header) return null;

  const bg = header.bg_color || '#FFFFFF';
  const fg = header.fg_color || '#0E0E23';
  const isCenter = header.logo_position === 'center';
  const isRight = header.logo_position === 'right';
  // Iter 188 — sticky-on-scroll. Never sticky in the editor canvas
  // (would fight with the drawer + hover chrome).
  const stickyStyle = header.sticky && !editable
    ? { position: 'sticky', top: 0, zIndex: 40 }
    : {};

  const Logo = header.logo_type === 'image' && header.logo_image ? (
    <img src={header.logo_image} alt="logo" className="h-8 max-w-[180px] object-contain" />
  ) : editable && E ? (
    <E
      block={{ id: '__site_header__' }}
      onEdit={(_id, _field, val) => onLogoTextChange?.(val)}
      field="logo_text"
      value={header.logo_text || 'Your Brand'}
      tag="h1"
      className="text-xl font-bold tracking-tight"
      fieldStyle={{ color: fg }}
      placeholder="Your Brand"
    />
  ) : (
    <h1 className="text-xl font-bold tracking-tight" style={{ color: fg }}>
      {header.logo_text || 'Your Brand'}
    </h1>
  );

  const Nav = (
    <nav className="flex items-center gap-6 text-sm font-medium">
      {items.map((it) => (
        <a
          key={it.id}
          href={it.href}
          className="hover:opacity-70 transition-opacity"
          style={{ color: fg }}
          data-testid={`site-header-nav-${it.id}`}
        >
          {it.label}
        </a>
      ))}
    </nav>
  );

  const Buttons = (
    <div className="flex items-center gap-2">
      {buttons.map((b) => {
        if (b.variant === 'search') {
          return (
            <div
              key={b.id}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm"
              style={{ borderColor: `${fg}20`, color: fg }}
              data-testid={`site-header-btn-${b.id}`}
            >
              <Search size={14} />
              <span className="opacity-70">{b.label || 'Search'}</span>
            </div>
          );
        }
        if (b.variant === 'ghost') {
          return (
            <a
              key={b.id}
              href={b.href || '#'}
              className="px-3 py-1.5 text-sm hover:opacity-70"
              style={{ color: fg }}
              data-testid={`site-header-btn-${b.id}`}
            >
              {b.label}
            </a>
          );
        }
        // 'shop' default — filled button
        return (
          <a
            key={b.id}
            href={b.href || '#'}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: fg, color: bg }}
            data-testid={`site-header-btn-${b.id}`}
          >
            <ShoppingBag size={13} />
            {b.label || 'Shop Now'}
          </a>
        );
      })}
      {/* Iter 189 — always show cart trigger on the public site (never in editor) */}
      {/* WYSIWYG — rendered in the editor too (inert) so the header keeps
          the exact same width/spacing in Edit and Preview. */}
      {editable ? (
        <span className="contents pointer-events-none" aria-hidden>
          <CartDrawerTrigger color={fg} />
          <SiteHeaderAccountLink color={fg} />
        </span>
      ) : (
        <>
          <CartDrawerTrigger color={fg} />
          <SiteHeaderAccountLink color={fg} />
        </>
      )}
    </div>
  );

  const editWrapProps = editable
    ? {
        onClick: (e) => {
          // Don't hijack clicks that hit an inline editor
          const isEditing = e.target.closest('[contenteditable="true"]');
          if (!isEditing) onEdit?.();
        },
        className: 'relative cursor-pointer group border-b',
        title: 'Click to edit site header',
      }
    : { className: 'relative border-b' };

  return (
    <header
      {...editWrapProps}
      style={{ backgroundColor: bg, borderColor: `${fg}12`, ...stickyStyle }}
      data-testid="site-header"
      data-editable={editable ? '1' : '0'}
      data-sticky={header.sticky ? '1' : '0'}
    >
      {editable && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.18em] font-bold bg-[#15152E]/95 text-[#A78BFA] border border-white/[0.1] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Header · click to edit
        </div>
      )}
      <div
        className={
          'max-w-7xl mx-auto px-6 py-4 flex items-center ' +
          (isCenter ? 'flex-col gap-3' : 'justify-between')
        }
      >
        {isCenter ? (
          <>
            <div>{Logo}</div>
            <div className="flex items-center justify-between w-full">
              <div className="flex-1">{Nav}</div>
              {buttons.length > 0 && <div className="flex-shrink-0">{Buttons}</div>}
            </div>
          </>
        ) : isRight ? (
          <>
            <div className="flex-1">{Nav}</div>
            <div className="flex-shrink-0 mx-6">{Logo}</div>
            {buttons.length > 0 && <div>{Buttons}</div>}
          </>
        ) : (
          <>
            <div className="flex-shrink-0">{Logo}</div>
            <div className="flex-1 flex justify-center">{Nav}</div>
            {buttons.length > 0 && <div className="flex-shrink-0">{Buttons}</div>}
          </>
        )}
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  <SiteFooter />
 * ─────────────────────────────────────────────────────────── */
export function SiteFooter({ footer, editable = false, onEdit, E, onLogoTextChange }) {
  if (!footer) return null;
  const bg = footer.bg_color || '#0E0E23';
  const fg = footer.fg_color || '#FFFFFF';
  const cols = footer.columns || [];
  const social = footer.social_links || [];
  const policies = footer.policy_links || [];
  const year = new Date().getFullYear();
  const brand = footer.logo_text || 'Your Brand';
  const copyright = (footer.copyright_text || '')
    .replace(/\{year\}/g, year)
    .replace(/\{brand\}/g, brand);

  const Logo = footer.logo_type === 'image' && footer.logo_image ? (
    <img src={footer.logo_image} alt="logo" className="h-8 max-w-[180px] object-contain mb-3" />
  ) : editable && E ? (
    <E
      block={{ id: '__site_footer__' }}
      onEdit={(_id, _field, val) => onLogoTextChange?.(val)}
      field="logo_text"
      value={footer.logo_text || 'Your Brand'}
      tag="p"
      className="text-2xl font-bold tracking-tight mb-2"
      fieldStyle={{ color: fg }}
      placeholder="Your Brand"
    />
  ) : (
    <p className="text-2xl font-bold tracking-tight mb-2" style={{ color: fg }}>
      {brand}
    </p>
  );

  const colClass =
    footer.layout === '4col' ? 'grid-cols-2 md:grid-cols-4'
    : footer.layout === '2col' ? 'grid-cols-1 md:grid-cols-2'
    : footer.layout === 'stacked' ? 'grid-cols-1'
    : 'grid-cols-1 md:grid-cols-3';

  const editWrapProps = editable
    ? {
        onClick: (e) => {
          const isEditing = e.target.closest('[contenteditable="true"]');
          if (!isEditing) onEdit?.();
        },
        className: 'relative cursor-pointer group',
        title: 'Click to edit site footer',
      }
    : { className: 'relative' };

  return (
    <footer
      {...editWrapProps}
      style={{ backgroundColor: bg, color: fg }}
      data-testid="site-footer"
      data-editable={editable ? '1' : '0'}
    >
      {editable && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.18em] font-bold bg-[#15152E]/95 text-[#A78BFA] border border-white/[0.1] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Footer · click to edit
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className={`grid ${colClass} gap-8`}>
          {/* Brand column */}
          <div className="min-w-0">
            {Logo}
            {footer.tagline && (
              <p className="text-sm opacity-70 mt-1 mb-4 max-w-xs">{footer.tagline}</p>
            )}
            {social.length > 0 && (
              <div className="flex items-center gap-3 mt-4">
                {social.map((s) => {
                  const Icon = SOCIAL_ICON[(s.platform || '').toLowerCase()] || Instagram;
                  return (
                    <a
                      key={s.id}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                      style={{ backgroundColor: `${fg}15`, color: fg }}
                      data-testid={`site-footer-social-${s.id}`}
                      aria-label={s.platform}
                    >
                      <Icon size={14} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Columns */}
          {cols.slice(0, footer.layout === '4col' ? 3 : cols.length).map((col) => (
            <div key={col.id} className="min-w-0">
              <p className="text-xs uppercase tracking-[0.18em] font-bold mb-3 opacity-80">
                {col.title}
              </p>
              <ul className="space-y-2">
                {(col.links || []).map((l, i) => (
                  <li key={i}>
                    <a
                      href={l.href}
                      className="text-sm opacity-70 hover:opacity-100 transition-opacity"
                      style={{ color: fg }}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar — policies + copyright */}
        <div className="mt-12 pt-6 border-t flex flex-col md:flex-row items-center justify-between gap-4"
             style={{ borderColor: `${fg}15` }}>
          <p className="text-xs opacity-60">{copyright}</p>
          {policies.length > 0 && (
            <div className="flex items-center gap-4 flex-wrap justify-center">
              {policies.map((p) => (
                <a
                  key={p.id}
                  href={p.href}
                  className="text-xs opacity-60 hover:opacity-100 transition-opacity"
                  style={{ color: fg }}
                  data-testid={`site-footer-policy-${p.id}`}
                >
                  {p.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
